/**
 * WeKIT Skills Graph — seed nodes.
 *
 * Every node is a pointer into open standards, never a proprietary
 * taxonomy: `esco_search` resolves the node against the public ESCO REST
 * API (skills/competences pillar) at runtime, and the resolved concept URI
 * is cached back into `public.skills_taxonomy`. ISCO-08 codes are recorded
 * only where the association is a defensible prior; O*NET element ids are
 * left empty until the O*NET Web Services key is live.
 *
 * Nothing here invents an ESCO URI. Unresolved nodes carry
 * provenance: "unmapped" and are flagged in the UI, not silently faked.
 */

import type { SkillNode, SkillDomain } from "./types";

function node(
  skill_id: string,
  label: string,
  domain: SkillDomain,
  transversal: boolean,
  esco_search: string,
  isco_code?: string,
  esco_type: "skill/competence" | "knowledge" | "transversal" = "skill/competence",
): SkillNode {
  return {
    skill_id,
    label,
    domain,
    transversal,
    esco_search,
    taxonomy: {
      esco_type: transversal ? "transversal" : esco_type,
      isco_code,
      provenance: isco_code ? "isco_prior" : "unmapped",
    },
  };
}

export const SKILL_CATALOGUE: SkillNode[] = [
  // ---- digital -----------------------------------------------------------
  node("digital_literacy", "Digital literacy", "digital", true, "digital literacy"),
  node("spreadsheets", "Spreadsheets", "digital", false, "use spreadsheets software", "4131", "knowledge"),
  node("web_development", "Web development", "digital", false, "develop website", "2513"),
  node("cloud_deployment", "Cloud deployment", "digital", false, "deploy cloud resources", "2519"),
  node("version_control", "Version control", "digital", false, "use version control systems", "2512"),
  node("cyber_hygiene", "Cyber safety", "digital", false, "manage IT security compliances", "2529", "knowledge"),
  node("no_code_tools", "No-code building", "digital", false, "use no-code development tools"),

  // ---- data --------------------------------------------------------------
  node("data_analysis", "Data analysis", "data", false, "perform data analysis", "2120"),
  node("sql_queries", "Databases and SQL", "data", false, "use query languages", "2521", "knowledge"),
  node("statistics", "Statistics", "data", false, "statistics", "2120", "knowledge"),
  node("data_visualisation", "Data visualisation", "data", false, "deliver visual presentation of data"),
  node("python_data", "Python for data", "data", false, "Python (computer programming)", "2511", "knowledge"),

  // ---- ai ----------------------------------------------------------------
  node("prompting", "Prompting", "ai", false, "use artificial intelligence tools"),
  node("ai_tool_usage", "AI tool fluency", "ai", false, "artificial intelligence", "2519", "knowledge"),
  node("ai_automation", "AI automation", "ai", false, "automate processes"),
  node("workflow_design", "Workflow design", "ai", false, "design workflows"),
  node("ml_foundations", "Machine learning foundations", "ai", false, "machine learning", "2120", "knowledge"),
  node("ai_ethics", "AI judgement and ethics", "ai", true, "apply ethical principles"),

  // ---- engineering -------------------------------------------------------
  node("programming", "Programming", "engineering", false, "computer programming", "2512", "knowledge"),
  node("api_integration", "APIs and integration", "engineering", false, "use application programming interfaces", "2512"),
  node("testing_debugging", "Testing and debugging", "engineering", false, "debug software", "2512"),
  node("making_hardware", "Making and prototyping", "engineering", false, "build prototypes", "3115"),
  node("systems_thinking", "Systems thinking", "engineering", true, "think analytically"),

  // ---- design ------------------------------------------------------------
  node("visual_design", "Visual design", "design", false, "create graphic designs", "2166"),
  node("ux_research", "User research", "design", false, "conduct user research", "2166"),
  node("content_creation", "Content creation", "design", false, "create content", "2642"),
  node("video_editing", "Video editing", "design", false, "edit digital moving images", "2654"),

  // ---- communication -----------------------------------------------------
  node("writing", "Writing", "communication", true, "write in a clear and structured way"),
  node("public_speaking", "Speaking to a room", "communication", true, "give a presentation"),
  node("storytelling", "Storytelling", "communication", true, "tell stories"),
  node("listening", "Listening well", "communication", true, "listen actively"),
  node("multilingual", "Working across languages", "communication", false, "communicate in foreign languages"),

  // ---- leadership --------------------------------------------------------
  node("team_leadership", "Leading a team", "leadership", true, "lead a team", "1120"),
  node("delegation", "Delegating", "leadership", true, "delegate activities"),
  node("conflict_resolution", "Handling conflict", "leadership", true, "manage conflicts"),
  node("decision_making", "Deciding under pressure", "leadership", true, "make decisions"),
  node("mentoring", "Coaching others", "leadership", true, "coach employees"),
  node("initiative", "Taking initiative", "leadership", true, "show initiative"),
  node("collaboration", "Collaborating", "leadership", true, "work in teams"),
  node("influence", "Persuading and influencing", "leadership", true, "persuade others"),

  // ---- enterprise --------------------------------------------------------
  node("project_management", "Project management", "enterprise", false, "manage project", "2421"),
  node("budgeting", "Handling money", "enterprise", false, "manage budgets", "2411"),
  node("selling", "Selling", "enterprise", false, "sell products", "2433"),
  node("entrepreneurship", "Starting something", "enterprise", false, "develop business plans", "1120"),
  node("marketing", "Marketing", "enterprise", false, "plan marketing campaigns", "2431"),
  node("customer_service", "Serving customers", "enterprise", true, "handle customer complaints", "4222"),

  // ---- care --------------------------------------------------------------
  node("teaching", "Teaching", "care", false, "teach", "2330"),
  node("caregiving", "Caring for people", "care", false, "provide personal care", "5322"),
  node("community_organising", "Organising people", "care", false, "organise community activities", "2635"),
  node("facilitation", "Facilitating groups", "care", true, "facilitate teamwork"),

  // ---- learning ----------------------------------------------------------
  node("self_directed_learning", "Learning on your own", "learning", true, "learn autonomously"),
  node("research", "Researching", "learning", true, "conduct research"),
  node("adaptability", "Adapting to change", "learning", true, "adapt to changing situations"),
  node("curiosity", "Curiosity", "learning", true, "show curiosity"),
  node("reflection", "Reflecting", "learning", true, "reflect on practice"),
];

const BY_ID = new Map(SKILL_CATALOGUE.map((s) => [s.skill_id, s]));

export function skill(skill_id: string): SkillNode | undefined {
  return BY_ID.get(skill_id);
}

export function skillsByDomain(domain: SkillDomain): SkillNode[] {
  return SKILL_CATALOGUE.filter((s) => s.domain === domain);
}
